/**
 * Working out what a line's pending quantity changes should look like after a
 * customer makes another one.
 *
 * CRM allows several amendments on one subscription line and does not stop them
 * overlapping, so "what will actually be delivered on the 12th" can become
 * genuinely ambiguous. Rather than leave that to chance, a new change is folded
 * into the existing ones here: it wins over anything it covers, trims anything
 * it partly covers, splits anything it lands inside, and merges back together
 * with any neighbour that ends up carrying the same quantity.
 *
 * Pure and date-string based (YYYY-MM-DD) so the cases below can be tested
 * without a network or a clock.
 */

export interface AmendmentInterval {
  /** The CRM amendment this came from; absent for one we are about to create. */
  id?: string
  quantity: number
  /** Inclusive first day the quantity applies. */
  from: string
  /** Inclusive last day, or null for "until further notice". */
  to: string | null
}

export type AmendmentOp =
  | { type: 'create', quantity: number, from: string, to: string | null }
  | { type: 'update', id: string, quantity: number, from: string, to: string | null }
  | { type: 'delete', id: string }

const shift = (date: string, days: number): string => {
  const [y, m, d] = date.split('-').map(Number)
  const next = new Date(y, m - 1, d + days)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`
}

/** null means open-ended, which sorts after every real date. */
const endsBefore = (end: string | null, date: string): boolean => end !== null && end < date
const endsAfter = (end: string | null, date: string): boolean => end === null || end > date

/**
 * Remove `cut` from `piece`, returning whatever is left (nothing, one side, or
 * both sides when the cut lands in the middle).
 */
const subtract = (piece: AmendmentInterval, cut: AmendmentInterval): AmendmentInterval[] => {
  // No overlap at all.
  if (endsBefore(piece.to, cut.from)) return [piece]
  if (cut.to !== null && piece.from > cut.to) return [piece]

  const remaining: AmendmentInterval[] = []

  if (piece.from < cut.from) {
    remaining.push({ ...piece, to: shift(cut.from, -1) })
  }

  if (cut.to !== null && endsAfter(piece.to, cut.to)) {
    // The tail keeps the original id only if the head didn't take it, so one
    // CRM record is never claimed by two intervals.
    remaining.push({
      ...piece,
      id: remaining.length > 0 ? undefined : piece.id,
      from: shift(cut.to, 1),
    })
  }

  return remaining
}

/** Join neighbours that carry the same quantity and sit end to end. */
const coalesce = (intervals: AmendmentInterval[]): AmendmentInterval[] => {
  const sorted = [...intervals].sort((a, b) => a.from.localeCompare(b.from))
  const merged: AmendmentInterval[] = []

  for (const interval of sorted) {
    const previous = merged[merged.length - 1]
    const isContiguous = previous
      && previous.quantity === interval.quantity
      && previous.to !== null
      && shift(previous.to, 1) === interval.from

    if (isContiguous) {
      previous.to = interval.to
      previous.id = previous.id ?? interval.id
    } else {
      merged.push({ ...interval })
    }
  }

  return merged
}

/**
 * The writes needed to apply `desired` to a line that already carries
 * `existing` pending changes.
 *
 * `baseQuantity` is what the line reverts to; an interval that matches it is
 * dropped rather than written, because an amendment to the current quantity
 * changes nothing and only clutters CRM.
 */
export const planAmendments = (
  existing: AmendmentInterval[],
  desired: AmendmentInterval,
  baseQuantity: number
): AmendmentOp[] => {
  const survivors = existing.flatMap(piece => subtract(piece, desired))
  const wanted = coalesce([...survivors, { ...desired, id: undefined }])
    .filter(interval => interval.quantity !== baseQuantity)

  // Reuse CRM records where we can, so a small edit doesn't churn every
  // amendment on the line into a delete-and-recreate.
  const spare = existing
    .map(e => e.id)
    .filter((id): id is string => !!id)
    .filter(id => !wanted.some(w => w.id === id))

  const ops: AmendmentOp[] = []

  for (const interval of wanted) {
    const id = interval.id ?? spare.shift()
    if (id) {
      ops.push({ type: 'update', id, quantity: interval.quantity, from: interval.from, to: interval.to })
    } else {
      ops.push({ type: 'create', quantity: interval.quantity, from: interval.from, to: interval.to })
    }
  }

  for (const id of spare) {
    ops.push({ type: 'delete', id })
  }

  return ops
}

/**
 * Keep every write inside the line's own dates (DF-389).
 *
 * The planner re-sends existing amendments as they are, so one that already
 * ran past the line's last delivery (or started before the line) was written
 * back unchanged, failing the ChargeBee sync and now refused by the backend
 * (DF-387). Each op is trimmed to [lineStart, lineEnd]; one left entirely
 * outside the line is dropped, or cancelled if it already exists in CRM.
 * An open-ended op stays open-ended: it simply stops when the line does.
 * Dates are YYYY-MM-DD; lineEnd is the inclusive last delivery.
 */
export const clampOpsToLine = (
  ops: AmendmentOp[],
  lineStart: string | null,
  lineEnd: string | null
): AmendmentOp[] =>
  ops.flatMap((op): AmendmentOp[] => {
    if (op.type === 'delete') return [op]

    const from = lineStart && op.from < lineStart ? lineStart : op.from
    const to = lineEnd && op.to !== null && op.to > lineEnd ? lineEnd : op.to
    const outside = (lineEnd !== null && from > lineEnd) || (to !== null && to < from)

    if (outside) return op.type === 'update' ? [{ type: 'delete', id: op.id }] : []

    return [{ ...op, from, to }]
  })
